import React, { useMemo, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { fmtEUR2, fmtDate } from "@/lib/format";
import { CARD, Label } from "@/components/common";

const MODES = [["cb", "CB"], ["especes", "Esp."], ["cheque", "Chq"], ["secu", "Sécu/CMU"], ["mutuelle", "Mut."], ["alma", "Alma"]];
const modeLine = (o) => MODES.filter(([k]) => Number(o[k]) > 0).map(([k, l]) => `${l} ${fmtEUR2(o[k])}`).join(" · ");

function DayRow({ d }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-1">
      <button type="button" onClick={() => setOpen(!open)} data-testid={`day-row-${d.date}`}
        className="flex w-full items-center justify-between gap-3 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-gray-50">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{fmtDate(d.date)} <span className="font-normal text-gray-400">· {d.patient_count} patient{d.patient_count > 1 ? "s" : ""}{d.site_name ? ` · ${d.site_name}` : ""}</span></p>
          <p className="truncate text-xs text-gray-400">{modeLine(d)}</p>
        </div>
        <span className="flex shrink-0 items-center gap-2 font-display font-bold text-emerald-700">{fmtEUR2(d.total)}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && (
        <div className="mx-2 mb-2 rounded-2xl bg-gray-50 p-3" data-testid={`day-detail-${d.date}`}>
          {d.patients.length === 0 ? <p className="text-xs text-gray-400">Pas de détail patient pour cette journée.</p> : d.patients.map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-3 py-1.5 text-sm">
              <div className="min-w-0"><p className="font-medium">{p.patient || "—"}</p><p className="text-xs text-gray-400">{modeLine(p)}{p.mutuelle_name ? ` (${p.mutuelle_name})` : ""}</p></div>
              <span className="shrink-0 font-semibold">{fmtEUR2(p.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function DaysCard({ days }) {
  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid="days-card">
      <Label className="mb-2">Détail des encaissements journaliers</Label>
      {days.length === 0 ? <p className="py-4 text-sm text-gray-400">Aucune journée saisie ce mois-ci.</p> :
        <div className="divide-y divide-gray-50">{days.map((d) => <DayRow key={d.id} d={d} />)}</div>}
    </div>
  );
}

export function PatientsCard({ patients }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => patients.filter((p) => p.patient.toLowerCase().includes(q.trim().toLowerCase())), [patients, q]);
  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid="patients-card">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <Label>Mes patients du mois ({patients.length})</Label>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input data-testid="patients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un patient" className="rounded-full pl-9" />
        </div>
      </div>
      {list.length === 0 ? <p className="py-4 text-sm text-gray-400">Aucun patient.</p> : (
        <div className="max-h-96 divide-y divide-gray-50 overflow-y-auto">
          {list.map((p) => (
            <div key={p.patient} className="flex items-center justify-between gap-3 py-2.5" data-testid="patient-row">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{p.patient}</p>
                <p className="truncate text-xs text-gray-400">{p.visits} passage{p.visits > 1 ? "s" : ""} · {modeLine(p)}</p>
              </div>
              <span className="shrink-0 font-display font-bold">{fmtEUR2(p.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
